import React, { useState, useEffect } from 'react';
import './Onboarding.css';  // Import the CSS file
import LanguageSelector from '../Components/LanguageSelector';
import ConfigureUserDisplay from '../Components/ConfigureUserDisplay';
import ProductSelector from '../Components/ProductSelector';
import PaymentsSelector from '../Components/PaymentsSelector';
import ContactInfoSelector from '../Components/ContactInfoSelector';
import SocialMediaSelector from '../Components/SocialMediaSelector';
import CreateCustomUrl from '../Components/CreateCustomUrl';
import DetailsApproval from '../Components/DetailsApproval';
import { useParams, useSearchParams } from 'react-router-dom';
import SaveToApi from '../utils/SaveToApi';
import { getAuthHeader } from '../utils/tokenManager';

const Onboarding = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [nextStepButton, setNextStepButton] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [languageSetting, setLanguageSetting] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState({});
  const [selectedContactInfo, setSelectedContactInfo] = useState({});
  const [selectedSocialMedia, setSelectedSocialMedia] = useState({});
  const [customUrl, setCustomUrl] = useState({ showInput: false, value: '', error: false, errorMessage: '' });
  const [userDisplay, setUserDisplay] = useState('');
  const [totalSteps, setTotalSteps] = useState(4);
  const [loginError, setLoginError] = useState(false);
  const [userId, setUserId] = useState(1); // Default to 1

  const { uuid } = useParams();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Parse user_id from URL query parameters
    const userIdParam = searchParams.get('user_id');
    if (userIdParam) {
      setUserId(parseInt(userIdParam, 10));
    }
  }, [searchParams]);

  const apiUrl = process.env.REACT_APP_DJANGO_API_URL;
  const webUrl = process.env.REACT_APP_WEB_URL;
  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const req_uri = `${apiUrl}/api/${uuid}/get_onboarding_user_data/?user_id=${userId}`;
        
        const response = await fetch(req_uri, {
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeader(),
          },
        });
        if (!response.ok) {
          setLoginError(true);
          throw new Error(`Error: ${response.status}`);
        }
        const data = await response.json();
        const payments_data = data.payments || {};
        const contact_data = data.contact || {};
        const social_data = data.social || {};
        let custom_user_id_data = data.custom_account_id || '';

        // Set user display name
        setUserDisplay(data.display_name || '');

        const payments_prez = Object.values(payments_data).every(value => value === null);
        const contacts_prez = Object.values(contact_data).every(value => value === null);
        const social_prez = Object.values(social_data).every(value => value === null);
        const data_selected_products = [];
        if (!payments_prez) { data_selected_products.push('Payments'); }
        if (!contacts_prez) { data_selected_products.push('Contact Info'); }
        if (!social_prez) { data_selected_products.push('Social'); }

        // Set selected products from API response
        setSelectedProducts(data.displayed_products || data_selected_products);

        for (const j of Object.keys(social_data)) {
          social_data[j] = { id: j, showInput: social_data[j] ? true : false, value: social_data[j] };
        }
        for (const j of Object.keys(contact_data)) {
          contact_data[j] = { id: j, showInput: contact_data[j] ? true : false, value: contact_data[j] };
        }
        for (const j of Object.keys(payments_data)) {
          payments_data[j] = { id: j, showInput: payments_data[j] ? true : false, value: payments_data[j] };
        }

        custom_user_id_data = { showInput: custom_user_id_data !== '' ? true : false, value: custom_user_id_data, error: false, errorMessage: '' };

        setSelectedPayments(payments_data);
        setSelectedContactInfo(contact_data);
        setSelectedSocialMedia(social_data);
        setCustomUrl(custom_user_id_data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, [uuid, userId]);

  var additional_instructions = [];

  const base_steps = [
    <><LanguageSelector onLanguageChange={(e) => { setLanguageSetting(e); setCurrentStep(currentStep + 1); }} languageSetting={languageSetting} /></>,
    <><ConfigureUserDisplay onUserDisplayChange={(display) => { setUserDisplay(display) }} userDisplay={userDisplay} languageSetting={languageSetting}/></>,
    <><ProductSelector onProductSelect={(e) => { handleProductSelect(e) }} selectedProducts={selectedProducts} setTotalSteps={setTotalSteps} totalSteps={totalSteps} languageSetting={languageSetting}/></>,
  ];

  const addl_steps_map = {
    "Payments": <PaymentsSelector onPaymentSelect={(e) => { handlePaymentsSelect(e) }} selectedPayments={selectedPayments} languageSetting={languageSetting}  />,
    "Contact Info": <ContactInfoSelector onContactSelect={(e) => { handleContactSelect(e) }} selectedContactInfo={selectedContactInfo} languageSetting={languageSetting} />,
    "Social": <SocialMediaSelector onSocialMediaSelect={(e) => { handleSocialSelect(e) }} selectedSocialMedia={selectedSocialMedia} languageSetting={languageSetting} />
  }

  const custom_url_step = userId === 1 ? 
    <CreateCustomUrl onCustomUrlSelect={(e) => setCustomUrl(e)} selectedCustomUrl={customUrl} accountId={uuid} languageSetting={languageSetting}/> 
    : null;

  const end_step = <DetailsApproval 
    selectedPayments={selectedPayments} 
    selectedContactInfo={selectedContactInfo} 
    selectedSocialMedia={selectedSocialMedia} 
    customUrl={customUrl} 
    account_id={uuid}
    selectedProducts={selectedProducts}
  />;

  const base_instructions = languageSetting === "ENGLISH" ? [
    "",
    "",
    "Tap what you want to share."
  ] : [
    "",
    "Configurar Su Nombre Para Mostrar Y Imagen",
    "Toca lo que quieras compartir."
  ];

  const addl_instructions = languageSetting === "ENGLISH" ? {
    "Payments": "Add your payment apps. The more links you give, the better chances for tips.",
    "Contact Info": "Share only what you want. You're in control.",
    "Social": "Choose the socials you want to share."
  } : {
    "Payments": "Agrega tus aplicaciones de pago. Cuantos más enlaces proporciones, mayores serán las posibilidades de recibir propinas.",
    "Contact Info": "Comparte solo lo que quieras. Tú tienes el control.",
    "Social": "Elige las redes sociales que quieres compartir."
  };

  const goNext = () => {
    if (currentStep < totalSteps + 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setCompleted(true);
    }
  };

  const goPrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      setCompleted(true);
    }
  };

  const handleProductSelect = (option) => {
    const isSelected = selectedProducts.includes(option);

    if (isSelected) {
      setSelectedProducts((prevProducts) =>
        prevProducts.filter((product) => product !== option)
      );
    } else {
      setSelectedProducts((prevProducts) => [...prevProducts, option]);
    }
  };

  const handlePaymentsSelect = (option) => {
    const paymentsCopy = JSON.parse(JSON.stringify(selectedPayments));
    paymentsCopy[option['id']] = option
    setSelectedPayments((selectedPayments) => ({ ...paymentsCopy }))
  };

  const handleContactSelect = (option) => {
    const contactsCopy = JSON.parse(JSON.stringify(selectedContactInfo));
    contactsCopy[option['id']] = option
    setSelectedContactInfo((selectedContactInfo) => ({ ...contactsCopy }))
  };

  const handleSocialSelect = (option) => {
    const socialCopy = JSON.parse(JSON.stringify(selectedSocialMedia));
    socialCopy[option['id']] = option
    setSelectedSocialMedia((selectedSocialMedia) => ({ ...socialCopy }))
  };

  const renderStepContent = () => {
    const additional_steps = selectedProducts.map((e) => { return addl_steps_map[e] });
    const steps = base_steps.concat(additional_steps);
    if (custom_url_step) {
      steps.push(custom_url_step);
    }
    const all_steps = steps.concat(end_step);
    return all_steps[currentStep];
  };

  const renderNextStepButton = () => {
    var show_next_step = false;
    switch (currentStep) {
      case 0:
        show_next_step = false;
        break;
      case 1:
        show_next_step = userDisplay.trim() !== '';
        break;
      case 2:
        show_next_step = selectedProducts.length > 0 ? true : false;
        break;
      case totalSteps - 1:
        // If this is the custom URL step (for user_id === 1) and there's an error, don't show next button
        if (userId === 1 && customUrl.error) {
          show_next_step = false;
        } else {
          show_next_step = true;
        }
        break;
      case totalSteps:
        if(typeof uuid !== 'undefined' && uuid) {
          SaveToApi({
            selectedPayments, 
            selectedContactInfo, 
            selectedSocialMedia, 
            customUrl, 
            account_id: uuid,
            user_id: userId,
            userDisplay: userDisplay,
            selectedProducts
          });
        }
        show_next_step = false;
        break;
      default:
        show_next_step = true;
    }
    return show_next_step;
  };

  const renderStepInstructions = () => {
    const final_addl_instructions = []
    selectedProducts.map(x => final_addl_instructions.push(addl_instructions[x]))
    const instructions = base_instructions.concat(final_addl_instructions);
    return (
      <div className="onboarding-instructions">
        <div className="main-instructions">{instructions[currentStep]}</div>
      </div>)
  };

  return (loginError ? <div>You Do Not Have Access</div> :
    <div className="onboarding-container">
      {renderStepInstructions()}
      {renderStepContent()}
      <br />
      {(currentStep !== 0 || renderNextStepButton()) && (
        <div className="next-onboarding-buttons-container">
          {currentStep !== 0 && (
            <div className="next-button" onClick={goPrevious}>
              {languageSetting === "ENGLISH" ? 'Previous' : 'Anterior'}
            </div>
          )}

          {renderNextStepButton() && (
            <>
              <div className="next-button-spacer"></div>
              <div className="next-button" onClick={goNext}>
                {languageSetting === "ENGLISH" ? 'Next' : 'Siguiente'}
              </div>
            </>
          )}
        </div>
      )}

    </div>
  );
};

export default Onboarding;
